import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import RequestCalculationForm from '../components/RequestCalculationForm';
import { newsAPI } from '../lib/api';
import { NEWS_HUB_PATH, buildNewsHubPath, getCategoryMeta } from '../lib/newsPublications';

const siteBase = (process.env.REACT_APP_BASE_URL || 'https://antologia.tech').replace(/\/$/, '');

function buildPublicationStructuredData(post, canonicalUrl) {
  const blocks = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Главная', item: `${siteBase}/` },
        { '@type': 'ListItem', position: 2, name: 'Новости', item: `${siteBase}${NEWS_HUB_PATH}` },
        {
          '@type': 'ListItem',
          position: 3,
          name: getCategoryMeta(post.category).label,
          item: `${siteBase}${buildNewsHubPath(post.category)}`,
        },
        { '@type': 'ListItem', position: 4, name: post.title, item: canonicalUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'NewsArticle',
      headline: post.title,
      description: post.metaDescription || post.description,
      image: post.ogImage || post.mainImage || post.previewImage || post.image,
      datePublished: post.publishedAt,
      dateModified: post.updated_at || post.publishedAt,
      author: { '@type': 'Organization', name: post.author || 'Антология' },
      publisher: {
        '@type': 'Organization',
        name: 'Антология',
        logo: { '@type': 'ImageObject', url: `${siteBase}/logo.png` },
      },
      mainEntityOfPage: canonicalUrl,
      inLanguage: 'ru-RU',
      articleSection: getCategoryMeta(post.category).label,
    },
  ];

  const faq = (post.faqItems || []).filter((f) => f.question && f.answer);
  if (faq.length) {
    blocks.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faq.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    });
  }

  return blocks;
}

const NewsPublicationPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    newsAPI.getPublication(slug).then((data) => {
      if (active) {
        setPost(data);
        setLoading(false);
      }
    }).catch(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [slug]);

  const canonicalUrl = useMemo(() => {
    if (!post) return '';
    const path = post.canonicalPath || `/novosti-antologia/publikaciya/${post.slug}`;
    return `${siteBase}${path}`;
  }, [post]);

  const structuredData = useMemo(
    () => (post ? buildPublicationStructuredData(post, canonicalUrl) : null),
    [post, canonicalUrl],
  );

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 pt-24 px-4 text-muted-foreground">Загрузка...</main>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <SEOHead title="Публикация не найдена | Антология" noindex />
        <Header />
        <main className="flex-1 pt-24 max-w-3xl mx-auto px-4">
          <h1 className="text-3xl font-bold mb-4">Публикация не найдена</h1>
          <Link to={NEWS_HUB_PATH} className="text-accent hover:underline">← К новостям</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const takeaways = post.keyTakeaways || [];
  const heroImage = post.mainImage || post.previewImage || post.image;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEOHead
        title={`${post.metaTitle || post.title} | Антология`}
        description={post.metaDescription || post.description}
        keywords={post.metaKeywords}
        canonical={canonicalUrl}
        image={post.ogImage || heroImage}
        type="article"
        publishedTime={post.publishedAt}
        structuredData={structuredData}
      />
      <Header />

      <style>{`
        .news-article-content h2 {
          font-size: 1.5rem;
          font-weight: 700;
          margin-top: 2.5rem;
          margin-bottom: 1rem;
          color: var(--foreground);
          line-height: 1.3;
        }
        .news-article-content h3 {
          font-size: 1.25rem;
          font-weight: 700;
          margin-top: 2rem;
          margin-bottom: 0.75rem;
          color: var(--foreground);
          line-height: 1.4;
        }
        .news-article-content p {
          font-size: 1rem;
          line-height: 1.75;
          margin-bottom: 1rem;
          color: var(--foreground);
          opacity: 0.9;
        }
        .news-article-content ul,
        .news-article-content ol {
          margin-top: 0.5rem;
          margin-bottom: 1rem;
          padding-left: 1.5rem;
        }
        .news-article-content li {
          font-size: 1rem;
          line-height: 1.75;
          margin-bottom: 0.25rem;
          color: var(--foreground);
          opacity: 0.9;
        }
        .news-article-content strong {
          color: var(--foreground);
          font-weight: 700;
        }
        .news-article-content a {
          color: hsl(var(--accent));
          text-decoration: none;
        }
        .news-article-content a:hover {
          text-decoration: underline;
        }
      `}</style>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative bg-white">
        <article className="max-w-3xl mx-auto">
          <nav className="text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-accent">Главная</Link>
            {' / '}
            <Link to={NEWS_HUB_PATH} className="hover:text-accent">Новости</Link>
            {' / '}
            <Link to={buildNewsHubPath(post.category)} className="hover:text-accent">
              {getCategoryMeta(post.category).label}
            </Link>
          </nav>

          <p className="text-sm text-accent font-medium mb-2">
            {getCategoryMeta(post.category).label}
            {post.date ? ` • ${post.date}` : ''}
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold mb-6">{post.title}</h1>

          {post.geoSummary && (
            <p className="text-lg text-foreground/90 leading-relaxed mb-8 border-l-4 border-accent pl-5 py-1 bg-accent/5 rounded-r-lg">
              {post.geoSummary}
            </p>
          )}

          {heroImage && (
            <div className="mb-8 rounded-xl overflow-hidden bg-muted">
              <img src={heroImage} alt={post.title} className="w-full h-auto object-cover" />
            </div>
          )}

          {takeaways.length > 0 && (
            <section className="mb-10 rounded-xl bg-accent/5 border border-accent/20 p-6">
              <h2 className="text-xl font-bold mb-4 text-foreground">Ключевые выводы</h2>
              <ul className="space-y-3 text-muted-foreground">
                {takeaways.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-accent" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {post.fullText && (
            <div
              className="news-article-content"
              dangerouslySetInnerHTML={{ __html: post.fullText }}
            />
          )}

          {(post.faqItems || []).length > 0 && (
            <section className="mt-12 space-y-4">
              <h2 className="text-2xl font-bold text-foreground mb-6">Частые вопросы</h2>
              {post.faqItems.map((item) => (
                <div key={item.question} className="rounded-xl border border-border/50 bg-card p-5">
                  <h3 className="font-semibold text-lg text-foreground mb-2">{item.question}</h3>
                  <p className="text-muted-foreground leading-relaxed">{item.answer}</p>
                </div>
              ))}
            </section>
          )}

          <div className="mt-12 pt-6 border-t border-border/50">
            <Link to={buildNewsHubPath(post.category)} className="text-accent hover:underline font-medium">
              ← Все материалы: {getCategoryMeta(post.category).label}
            </Link>
          </div>
        </article>

        <div className="max-w-3xl mx-auto mt-16 mb-4">
          <RequestCalculationForm
            variant="inline"
            showTaskDescription={true}
            title="Обсудить вашу задачу"
            description="Опишите, что вас интересует, и мы предложим оптимальное решение под ваш бюджет и сроки"
            submitLabel="Отправить запрос"
          />
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NewsPublicationPage;
